import { useEffect, useCallback, useState, useRef } from "react";
import { Loader, Message, Button } from "semantic-ui-react";
import { ICrewMember, IIDDataSet } from "../../models/crewMember";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CertificatesTable } from "../Certificates/CertificatesTable";
import { SRBTable } from "../SRB/SRBTable";
import { AuthorizationTable } from "../Authorizations/AuthorizationTable";
import { CrewMemberDetails } from "./CrewMemberDetails";
import { useIntl } from "react-intl";
import { toast } from "../..";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { UserRole } from "../../actions/authentificationActions";
import {
    getCrewDetailResponseFromECDB,
    getCrewMemberByCid,
    getCrewMemberByGuid,
    sendCrewDetailRequestToECDB,
} from "../../api/crewMembers";
import { DeleteCrewMemberModal } from "./DeleteCrewMemberModal";
import { CertificateStatus, CertificateType, ICertificate } from "../../models/certificate";
import { IAuthorization } from "../../models/authorization";
import { ISRB } from "../../models/srb";
import "./CrewMember.css";

export const CrewMember = () => {
    const history = useHistory();
    const user = useSelector((state: AppState) => state.user);
    const loc = useLocation() as any;
    const params: { guid: string } = useParams();
    const [loading, setLoading] = useState(false);
    const setInitCrewMember = () => {
        if (loc.state && loc.state.idDataSet) {
            const ecdbIdDataSet = loc.state.idDataSet as IIDDataSet;
            const ecdbCrewMember: ICrewMember = {
                guid: "ECDB_CREW_MEMBER",
                cid: ecdbIdDataSet.cid,
                lastUpdated: ecdbIdDataSet.lastUpdated,
                idDataSets: ([] as IIDDataSet[]).concat(ecdbIdDataSet),
                certificates: [],
                authorizations: [],
                serviceRecordBooks: [],
                requestFeedbacks: [],
                ecdbAcknowledged: true,
                qrBase64: "",
            };
            return ecdbCrewMember;
        } else return undefined;
    };
    const [crewMemberDetails, setCrewMemberDetails] = useState(setInitCrewMember());
    const [certificates, setCertificates] = useState<ICertificate[]>([]);
    const [authorizations, setAuthorizations] = useState<IAuthorization[]>([]);
    const [srbs, setSrbs] = useState<ISRB[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [fetchingEcdbResponses, setFetchingEcdbResponses] = useState(false);
    const requestId = useRef<string>(
        loc.state && !!loc.state.detailRequestId ? loc.state.detailRequestId : ""
    );
    const intl = useIntl();

    const fetchCrewMember = useCallback(
        async (useLoading: boolean = true) => {
            useLoading && setLoading(true);
            try {
                const res = await getCrewMemberByGuid(params.guid);
                setCrewMemberDetails({
                    ...res,
                    certificates: [],
                    authorizations: [],
                    serviceRecordBooks: [],
                });
                updateStatesHelper(res);
            } catch {
                toast(intl.formatMessage({ id: "unableToFetchCrewMemberDetails" }), false);
            } finally {
                setLoading(false);
            }
        },
        [params.guid, intl]
    );

    const handleSendCrewDetailRequestToECDB = useCallback(async () => {
        if (!!crewMemberDetails?.cid && !requestId.current) {
            setFetchingEcdbResponses(true);
            try {
                const targetRequestId = await sendCrewDetailRequestToECDB(crewMemberDetails.cid);
                requestId.current = targetRequestId.replace(/"/g, "").replace(/-/g, "");
            } catch {
                toast(intl.formatMessage({ id: "unableToFetchDataSets" }), false);
            }
        }
    }, [crewMemberDetails?.cid, intl]);

    const handleCrewDetailResponseFromECDB = useCallback(async () => {
        if (!requestId.current) await handleSendCrewDetailRequestToECDB();
        const res = await getCrewDetailResponseFromECDB(requestId.current);
        if (res) updateStatesHelper(res);
    }, [handleSendCrewDetailRequestToECDB]);

    useEffect(() => {
        if (loc.state && loc.state.idDataSet) {
            const ecdbIdDataSet = loc.state.idDataSet as IIDDataSet;
            getCrewMemberByCid(ecdbIdDataSet.cid).then(
                res => res && "guid" in res && history.push("/crew-members/" + res.guid)
            );
        } else {
            fetchCrewMember();
            const intervalId = setInterval(() => {
                fetchCrewMember(false);
            }, 15000);
            return () => clearInterval(intervalId);
        }
    }, [fetchCrewMember, loc.state, history]);

    useEffect(() => {
        handleCrewDetailResponseFromECDB();
        let count = 1;
        const intervalId = setInterval(() => {
            if (count >= 24) {
                setFetchingEcdbResponses(false);
                clearInterval(intervalId);
            }
            handleCrewDetailResponseFromECDB();
            count++;
        }, 5000);
        return () => clearInterval(intervalId);
    }, [handleCrewDetailResponseFromECDB]);

    const updateStatesHelper = (data: ICrewMember) => {
        setCertificates(prevCerts => {
            data.certificates.forEach(c => {
                if (!prevCerts.some(x => x.qualificationID === c.qualificationID)) {
                    c.guid = c.issuingAuthority.includes("AT")
                        ? c.guid
                        : "ECDB_CERT_" + Math.random();
                    prevCerts.push(c);
                } else {
                    const i = prevCerts.findIndex(x => x.qualificationID === c.qualificationID);
                    if (i > -1) prevCerts[i] = { ...c };
                }
            });
            return [...prevCerts];
        });
        setAuthorizations(prevAuths => {
            data.authorizations.forEach(a => {
                if (!prevAuths.some(x => x.qualificationID === a.qualificationID)) {
                    a.guid = a.issuingAuthority.includes("AT")
                        ? a.guid
                        : "ECDB_AUTH_" + Math.random();
                    prevAuths.push(a);
                } else {
                    const i = prevAuths.findIndex(x => x.qualificationID === a.qualificationID);
                    if (i > -1) prevAuths[i] = { ...a };
                }
            });
            return [...prevAuths];
        });
        setSrbs(prevSrbs => {
            data.serviceRecordBooks.forEach(s => {
                if (!prevSrbs.some(x => x.serialNumber === s.serialNumber)) {
                    s.guid = s.issuingAuthority.includes("AT")
                        ? s.guid
                        : "ECDB_SRB_" + Math.random();
                    prevSrbs.push(s);
                } else {
                    const i = prevSrbs.findIndex(x => x.serialNumber === s.serialNumber);
                    if (i > -1) prevSrbs[i] = { ...s };
                }
            });
            return [...prevSrbs];
        });
    };

    useEffect(() => {
        return () => {
            requestId.current = "";
        };
    }, []);

    return (
        <div className="crew-member-container">
            <div className="container-header">
                <h1>{intl.formatMessage({ id: "viewCrewMemberDetails" })}</h1>
                {fetchingEcdbResponses && (
                    <strong id="ecdb-fetching-message">
                        <Loader active inline size="small" />
                        {intl.formatMessage({ id: "fetchingFromEcdb" })}
                    </strong>
                )}
                {user.role === UserRole.PowerUser &&
                    !crewMemberDetails?.guid.includes("ECDB_CREW_MEMBER") && (
                        <Button
                            negative
                            basic
                            content={intl.formatMessage({ id: "deleteCrewMemberData" })}
                            onClick={() => setModalOpen(true)}
                        />
                    )}
            </div>
            {loading ? (
                <Loader active inline="centered" size="big" />
            ) : crewMemberDetails ? (
                <div className="crew-member-details-container">
                    <CrewMemberDetails
                        crewMember={crewMemberDetails}
                        fetchCrewMember={() => fetchCrewMember()}
                    />
                    <CertificatesTable
                        crewMember={crewMemberDetails}
                        certificates={certificates}
                        fetchCrewMember={() => fetchCrewMember()}
                        dateOfBirth={crewMemberDetails.idDataSets[0].dateOfBirth}
                    />
                    {user.role !== UserRole.OfficeUserViadonau && (
                        <AuthorizationTable
                            authorizations={authorizations}
                            eligibleCertificates={certificates.filter(
                                c =>
                                    (c.status === CertificateStatus.Active ||
                                        c.status === CertificateStatus.Suspended) &&
                                    c.type === CertificateType.UcqBoatmaster
                            )}
                            crewMember={crewMemberDetails}
                            fetchCrewMember={() => fetchCrewMember()}
                        />
                    )}
                    {user.role !== UserRole.OfficeUserViadonau && (
                        <SRBTable
                            srbs={srbs}
                            crewMember={crewMemberDetails}
                            fetchCrewMember={() => fetchCrewMember()}
                        />
                    )}
                    <DeleteCrewMemberModal
                        open={modalOpen}
                        closeModal={() => setModalOpen(false)}
                        crewMemberGuid={crewMemberDetails.guid}
                    />
                </div>
            ) : (
                <Message
                    icon="info circle"
                    header={intl.formatMessage({ id: "crewMemberNotFound" })}
                />
            )}
        </div>
    );
};
